import { DirtySubmitHandler, useCustomForm } from "@/common/hooks/useCustomForm";
import { GeoTransformers } from "@/common/services/geoTransformers";
import { formatCurrency } from "@/common/utils/formatters"
import { PrivateCompanyLeadsFunnelUpdateValidationSchema, updateSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema"
import { PrivateCompanyLeadData } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import useUpdatePrivateCompanyLead from "./useUpdatePrivateCompanyLead";
import { FormTransformers } from "@/common/utils/formTransformers";
import useUpdatePrivateCompanyLeadManagers from "./useUpdatePrivateCompanyLeadManagers";
import { useNavigate } from "react-router-dom";

const usePrivateCompanyLeadForm = ({
  privateCompanyLead
}: {
  privateCompanyLead: PrivateCompanyLeadData
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState(false);

  const formMethods = useCustomForm<PrivateCompanyLeadsFunnelUpdateValidationSchema>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      commercial_manager: privateCompanyLead?.commercial_manager ? {
        label: privateCompanyLead?.commercial_manager.full_name,
        value: privateCompanyLead?.commercial_manager.id,
      } : undefined,
      commercial_manager_substitute: privateCompanyLead?.commercial_manager_substitute ? {
        label: privateCompanyLead?.commercial_manager_substitute.full_name,
        value: privateCompanyLead?.commercial_manager_substitute.id,
      } : undefined,
      operational_information_attributes: {
        expected_credit: privateCompanyLead?.operational_information?.expected_credit ? formatCurrency(privateCompanyLead?.operational_information?.expected_credit) : undefined,
        industry_segment: privateCompanyLead?.operational_information?.industry_segment?.id ? { label: privateCompanyLead?.operational_information?.industry_segment?.display_name, value: privateCompanyLead?.operational_information?.industry_segment?.id } : undefined,
        geos: GeoTransformers.mapToGeo()(privateCompanyLead?.operational_information?.geo_geoables),
        product_categories: privateCompanyLead?.operational_information?.product_categories?.map((product) => ({ label: product.display_name, value: product.id })) || []
      },
      general_information: {
        ...privateCompanyLead?.general_information,
      },
      address_attributes: {
        ...privateCompanyLead?.address,
      },
    },
    shouldOnlyTriggerDirtyFieldsOnSubmit: true,
  });

  const updateLeadOnSuccess = useCallback(() => {
    setServerError(false);
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLead.id] })
    formMethods.resetDirtyFields();
  }, [setServerError, queryClient, privateCompanyLead.id]);

  const redirectOnSuccess = useCallback((path: string) => {
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLead.id] })
    navigate(path)
  }, [queryClient, privateCompanyLead.id]);

  const passedEveryRequirement = useMemo(
    () => privateCompanyLead?.requirement_list_summary?.steps?.every((step) => step.passed),
    [privateCompanyLead]
  );

  const onError = () => setServerError(true);

  const {
    mutate: updatePrivateCompanyLead,
    isPending: updatePrivateCompanyLeadIsPending,
  } = useUpdatePrivateCompanyLead({
    id: privateCompanyLead.id,
    privateCompanyLead,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<PrivateCompanyLeadsFunnelUpdateValidationSchema>({
      setError: formMethods.setError,
    }),
  })

  const {
    mutate: updatePrivateCompanyLeadManagers,
    isPending: updatePrivateCompanyLeadManagersIsPending,
  } = useUpdatePrivateCompanyLeadManagers({
    id: privateCompanyLead.id,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<PrivateCompanyLeadsFunnelUpdateValidationSchema>({
      setError: formMethods.setError,
    }),
  });

  const submitManagers = formMethods.handleDirtySubmit(updatePrivateCompanyLeadManagers);

  const onSubmit: DirtySubmitHandler<PrivateCompanyLeadsFunnelUpdateValidationSchema> = (data) => {
    updatePrivateCompanyLead(data)
  }

  const submit = formMethods.handleDirtySubmit(onSubmit);

  const submittableOnBlur = useCallback((e: React.FocusEvent<HTMLInputElement, Element>, onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void) => {
    if (e.target.value === '') {
      return
    }
    onBlur(e)
    submit()
  }, [submit]);

  useEffect(formMethods.resetDirtyFields, []);

  return {
    serverError,
    setServerError,
    formMethods,
    updateLeadOnSuccess,
    redirectOnSuccess,
    onError,
    passedEveryRequirement,
    submitManagers,
    submit,
    submittableOnBlur,
    updatePrivateCompanyLead,
    updatePrivateCompanyLeadManagers,
    updatePrivateCompanyLeadIsPending,
    updatePrivateCompanyLeadManagersIsPending,
  };
}

export default usePrivateCompanyLeadForm
